<template>
  <!-- prettier-ignore -->
  <component
    :is="tag"
    :class="classTypography"
    v-on="$listeners">
    <slot />
  </component>
</template>

<script>
const TYPE = {
  heading01: 'heading01',
  heading02: 'heading02',
  heading03: 'heading03',
  heading04: 'heading04',
  heading05: 'heading05',
  heading06: 'heading06',
  heading07: 'heading07',
  heading08: 'heading08',
  body01: 'body01',
  body02: 'body02',
  body03: 'body03',
  body04: 'body04',
  body05: 'body05',
  body06: 'body06',
}

const TYPE_DEFAULT = TYPE.body05

const COLOR = {
  black: 'black',
  darkGray: 'darkGray',
  white: 'white',
  green: 'green',
  lightGreen: 'lightGreen',
  red: 'red',
}

const COLOR_DEFAULT = COLOR.black

const TEXT_ALIGN = {
  left: 'left',
  center: 'center',
  right: 'right',
}

const TEXT_ALIGN_DEFAULT = TEXT_ALIGN.left

const TEXT_TRANSFORM = {
  none: 'none',
  uppercase: 'uppercase',
  lowercase: 'lowercase',
  capitalize: 'capitalize',
}

const TEXT_TRANSFORM_DEFAULT = TEXT_TRANSFORM.none

export default {
  name: 'OvpTypography',

  props: {
    tag: {
      type: String,
      default: 'div',
    },
    type: {
      type: [String, Object],
      default: TYPE_DEFAULT,
      validator: val => {
        if (typeof val === 'string') {
          return Object.keys(TYPE).includes(val)
        }
        if (typeof val === 'object') {
          return Object.values(val).some(v => Object.keys(TYPE).includes(v))
        }
      },
    },
    color: {
      type: [String, Object],
      default: COLOR_DEFAULT,
      validator: val => {
        if (typeof val === 'string') {
          return Object.keys(COLOR).includes(val)
        }
        if (typeof val === 'object') {
          return Object.values(val).some(v => Object.keys(COLOR).includes(v))
        }
      },
    },
    textAlign: {
      type: [String, Object],
      default: TEXT_ALIGN_DEFAULT,
      validator: val => {
        if (typeof val === 'string') {
          return Object.keys(TEXT_ALIGN).includes(val)
        }
        if (typeof val === 'object') {
          return Object.values(val).some(v => Object.keys(TEXT_ALIGN).includes(v))
        }
      },
    },
    textTransform: {
      type: [String, Object],
      default: TEXT_TRANSFORM_DEFAULT,
      validator: val => {
        if (typeof val === 'string') {
          return Object.keys(TEXT_TRANSFORM).includes(val)
        }
        if (typeof val === 'object') {
          return Object.values(val).some(v => Object.keys(TEXT_TRANSFORM).includes(v))
        }
      },
    },
  },

  computed: {
    mqType() {
      return this.mapPropToCurrentMq(this.type, TYPE_DEFAULT)
    },
    mqColor() {
      return this.mapPropToCurrentMq(this.color, COLOR_DEFAULT)
    },
    mqTextAlign() {
      return this.mapPropToCurrentMq(this.textAlign, TEXT_ALIGN_DEFAULT)
    },
    mqTextTransform() {
      return this.mapPropToCurrentMq(this.textTransform, TEXT_TRANSFORM_DEFAULT)
    },
    classTypography() {
      const type = TYPE[this.mqType]
      const color = COLOR[this.mqColor] === COLOR_DEFAULT ? [] : `c-${COLOR[this.mqColor]}`
      const textAlign = TEXT_ALIGN[this.mqTextAlign] === TEXT_ALIGN_DEFAULT ? [] : `ta-${TEXT_ALIGN[this.mqTextAlign]}`
      const textTransform =
        TEXT_TRANSFORM[this.mqTextTransform] === TEXT_TRANSFORM_DEFAULT
          ? []
          : `tt-${TEXT_TRANSFORM[this.mqTextTransform]}`

      return [type, color, textAlign, textTransform].flat()
    },
  },

  methods: {
    mapPropToCurrentMq(prop, fallback) {
      if (typeof prop === 'string') {
        return prop
      }

      if (typeof prop === 'object') {
        const currentType = Object.entries(prop).find(([mq]) => this.$mqAliases[mq])

        if (currentType) {
          return currentType[1]
        }

        // fallback to the default type
        if (prop._) {
          return prop._
        }
      }

      // fallback if no match is found
      return fallback
    },
  },
}
</script>

<style scoped>
.heading03 {
  font-size: 25px;
  font-weight: lighter;
  line-height: 30px;
}

.heading04 {
  font-size: 20px;
  line-height: 25px;
  letter-spacing: 0;
  font-weight: bold;
}

.heading05 {
  font-size: 30px;
  line-height: 36px;
  letter-spacing: -0.2px;
  font-weight: bold;
}

.heading06 {
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.1px;
  font-weight: bold;
}

.body01 {
  font-size: 15px;
  line-height: 25px;
  letter-spacing: normal;
}

.body02 {
  font-size: 15px;
  line-height: 25px;
  letter-spacing: normal;
  font-weight: bold;
}

.body04 {
  font-size: 16px;
  line-height: 24px;
  font-weight: normal;
}

.body05 {
  font-size: 15px;
  line-height: 25px;
  letter-spacing: normal;
  font-style: italic;
}

.heading02 {
  font-size: 25px;
  line-height: 30px;
  letter-spacing: normal;
  font-weight: bold;
}
</style>
